/**
 * FloatingSidebar Component
 *
 * Fixed-width floating sidebar for desktop/tablet:
 * - Desktop (≥1024px): 240px wide, full labels
 * - Tablet (768-1023px): 64px wide, icon-only with tooltips
 * - Mobile (<768px): Hidden (replaced by MobileBottomTabs)
 *
 * Renders role-appropriate navigation:
 * - Admin: AccordionNavigation (collapsible categories)
 * - Guide/Customer: FlatNavigation (simple list)
 *
 * Epic: MVP 1.21.2 - Dashboard Design System & Notifications (SCRUM-459)
 * Component: US-1.21.2-03 - Floating Sidebar Desktop
 *
 * @component
 * @param {Object} props
 * @param {Array|Object} props.navigation - Navigation items (flat array or categorized)
 * @param {string} props.userRole - User role: 'admin' | 'guide' | 'customer'
 * @param {string} props.activeNavId - Currently active navigation item ID
 * @param {Function} props.onNavigate - Navigation callback function
 * @param {boolean} props.isCollapsed - Collapsed state (tablet icon-only)
 */

import React from 'react';
import PropTypes from 'prop-types';
import AccordionNavigation from './AccordionNavigation';
import FlatNavigation from './FlatNavigation';

const FloatingSidebar = ({
  navigation,
  userRole,
  activeNavId,
  onNavigate,
  isCollapsed = false
}) => {
  // Determine which navigation component to render
  const isAdmin = userRole === 'admin';
  const NavigationComponent = isAdmin ? AccordionNavigation : FlatNavigation;

  return (
    <aside style={{
      ...styles.sidebar,
      width: isCollapsed ? 'var(--sidebar-width-collapsed)' : 'var(--sidebar-width)',
    }}>
      <div style={styles.sidebarInner}>
        <NavigationComponent
          navigation={navigation}
          activeId={activeNavId}
          onNavigate={onNavigate}
          isCollapsed={isCollapsed}
        />
      </div>
    </aside>
  );
};

const styles = {
  sidebar: {
    position: 'sticky',
    top: 'var(--space-6)', // Stick position when scrolling
    marginTop: 'var(--space-6)', // Align initial position with mainContent padding
    height: 'fit-content',
    maxHeight: 'calc(100vh - var(--space-6) * 2)',
    flexShrink: 0,
    marginRight: 'var(--sidebar-gap)',
    transition: 'width var(--transition-base)',
  },

  sidebarInner: {
    backgroundColor: 'var(--color-white)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--space-4)',
    boxShadow: 'var(--shadow-md)',
    overflowY: 'auto',
    height: '100%',
  },
};

FloatingSidebar.propTypes = {
  navigation: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.object), // Flat navigation
    PropTypes.arrayOf(PropTypes.shape({   // Accordion navigation
      category: PropTypes.string,
      items: PropTypes.arrayOf(PropTypes.object),
    })),
  ]).isRequired,
  userRole: PropTypes.oneOf(['admin', 'guide', 'customer']).isRequired,
  activeNavId: PropTypes.string,
  onNavigate: PropTypes.func,
  isCollapsed: PropTypes.bool,
};

export default FloatingSidebar;
